/**
 * Filtros de coleção Andrade — material/pedra (tags + inferência no índice).
 * Namoro/prata: todos os produtos contam como Prata 925; diamante não aparece.
 * Contagens: cards visíveis; índice leve (?view=andrade-filter-index) em idle.
 */
class AndradeCollectionFilters extends HTMLElement {
  #media = null;
  #index = [];
  #cards = [];
  #selectedMaterial = new Set();
  #selectedStone = new Set();
  #selectedPromo = new Set();
  #gridObserver = null;

  connectedCallback() {
    this.#parseIndex();
    this.#bindSwatches();
    this.#restoreFromUrl();

    const boot = () => {
      this.#bindCards();
      this.#initCounts();
      this.#watchGrid();
      this.#syncAccordion();
      this.#applyFilters(false);
      this.#loadSlimIndex();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        boot();
      }, { once: true });
    } else {
      requestAnimationFrame(() => {
        boot();
      });
    }

    this.#media = window.matchMedia('(max-width: 749px)');
    this.#media.addEventListener('change', this.#onMediaChange);
  }

  disconnectedCallback() {
    this.#media?.removeEventListener('change', this.#onMediaChange);
    this.#gridObserver?.disconnect();
  }

  #onMediaChange = () => {
    this.#syncAccordion();
  };

  #parseIndex() {
    const node = this.querySelector('[data-andrade-filter-index]');
    if (!node) return;

    try {
      this.#index = JSON.parse(node.textContent || '[]');
    } catch {
      this.#index = [];
    }
  }

  async #loadSlimIndex() {
    const path = window.location.pathname.replace(/\/$/, '');
    if (!path.includes('/collections/')) return;

    const run = async () => {
      try {
        const res = await fetch(`${path}?view=andrade-filter-index`, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (!res.ok) return;

        const text = await res.text();
        const start = text.indexOf('[');
        const end = text.lastIndexOf(']');
        if (start < 0 || end <= start) return;

        const data = JSON.parse(text.slice(start, end + 1));
        const products = Array.isArray(data) ? data : [];
        if (!products.length) return;

        this.#index = products.map((row) => this.#normalizeRow(row));
        this.#initCounts();
      } catch {
        /* mantém contagens dos cards visíveis */
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        run();
      }, { timeout: 2500 });
    } else {
      window.setTimeout(run, 1);
    }
  }

  #isSilverOccasion() {
    return this.dataset.materialOccasion === 'silver';
  }

  #coerceRow(row) {
    let material = Array.isArray(row.material)
      ? row.material.filter(Boolean)
      : String(row.material || '')
          .split('|')
          .filter(Boolean);
    let stone = Array.isArray(row.stone)
      ? row.stone.filter(Boolean)
      : String(row.stone || '')
          .split('|')
          .filter(Boolean);
    let promo = Array.isArray(row.promo)
      ? row.promo.filter(Boolean)
      : String(row.promo || '')
          .split('|')
          .filter(Boolean);

    if (this.#isSilverOccasion()) {
      material = ['silver-925'];
      stone = stone.map((key) => (key === 'diamante' ? 'zirconia' : key));
    }

    return {
      ...row,
      id: String(row.id || ''),
      material,
      stone,
      promo,
    };
  }

  #normalizeRow(row) {
    return this.#coerceRow({
      id: String(row.id),
      material: Array.isArray(row.material)
        ? row.material.filter(Boolean)
        : String(row.material || '')
            .split('|')
            .filter(Boolean),
      stone: Array.isArray(row.stone)
        ? row.stone.filter(Boolean)
        : String(row.stone || '')
            .split('|')
            .filter(Boolean),
      promo: Array.isArray(row.promo)
        ? row.promo.filter(Boolean)
        : String(row.promo || '')
            .split('|')
            .filter(Boolean),
    });
  }

  #getGrid() {
    return document.querySelector('.collection-wrapper--andrade-filters .product-grid, results-list .product-grid');
  }

  #bindCards() {
    const grid = this.#getGrid();
    if (!grid) return;

    this.#cards = [...grid.querySelectorAll('.product-grid__item[data-product-id]')].map((el) =>
      this.#rowFromElement(el)
    );
  }

  #rowFromElement(el) {
    return this.#coerceRow({
      el,
      id: el.dataset.productId,
      material: (el.dataset.andradeFilterMaterial || '').split('|').filter(Boolean),
      stone: (el.dataset.andradeFilterStone || '').split('|').filter(Boolean),
      promo: (el.dataset.andradeFilterPromo || '').split('|').filter(Boolean),
    });
  }

  #watchGrid() {
    const grid = this.#getGrid();
    if (!grid) return;

    this.#gridObserver = new MutationObserver(() => {
      const known = new Set(this.#cards.map((c) => c.id));
      let changed = false;

      grid.querySelectorAll('.product-grid__item[data-product-id]').forEach((el) => {
        if (known.has(el.dataset.productId)) return;
        this.#cards.push(this.#rowFromElement(el));
        known.add(el.dataset.productId);
        changed = true;
      });

      if (changed) {
        this.#applyFilters(false);
      }
    });

    this.#gridObserver.observe(grid, { childList: true, subtree: true });
  }

  #getIndexRows() {
    if (!this.#index.length) return [];
    return this.#index.map((row) => this.#normalizeRow(row));
  }

  #getCardRows() {
    if (this.#cards.length) return this.#cards;

    const grid = this.#getGrid();
    if (!grid) return [];

    this.#cards = [...grid.querySelectorAll('.product-grid__item[data-product-id]')].map((el) =>
      this.#rowFromElement(el)
    );
    return this.#cards;
  }

  #initCounts() {
    this.querySelectorAll('[data-filter-type][data-filter-key]').forEach((btn) => {
      const type = btn.dataset.filterType;
      const key = btn.dataset.filterKey;
      const count = this.#countMatches(type, key, new Set(), new Set(), new Set());
      btn.dataset.filterCount = String(count);

      const countEl =
        btn.querySelector('.andrade-collection-filters__count') ||
        btn.querySelector('.andrade-collection-filters__swatch-count');
      if (countEl) countEl.textContent = `(${count})`;

      const selected = (type === 'stone' ? this.#selectedStone : this.#selectedMaterial).has(key);
      const unavailable = count === 0 && !selected;
      const item = btn.closest('.andrade-collection-filters__item');

      if (item) item.hidden = unavailable;
      btn.hidden = unavailable;
      btn.disabled = unavailable;
    });

    ['material', 'stone', 'promo'].forEach((groupType) => {
      const group = this.querySelector(`[data-filter-group="${groupType}"]`);
      if (!group) return;
      const visible = [...group.querySelectorAll(`[data-filter-type="${groupType}"]`)].some(
        (btn) => !btn.hidden
      );
      group.hidden = !visible;
      group.classList.toggle('is-ready', visible);
    });
  }

  #bindSwatches() {
    this.querySelectorAll('[data-filter-type][data-filter-key]').forEach((btn) => {
      btn.addEventListener('click', () => this.#toggle(btn));
    });
  }

  #setForType(type) {
    if (type === 'stone') return this.#selectedStone;
    if (type === 'promo') return this.#selectedPromo;
    return this.#selectedMaterial;
  }

  #toggle(btn) {
    if (btn.disabled) return;

    const type = btn.dataset.filterType;
    const key = btn.dataset.filterKey;
    const set = this.#setForType(type);

    if (set.has(key)) {
      set.delete(key);
      btn.classList.remove('andrade-collection-filters__swatch-item--active');
      btn.classList.remove('andrade-collection-filters__link--active');
      btn.setAttribute('aria-pressed', 'false');
    } else {
      set.add(key);
      btn.classList.add('andrade-collection-filters__swatch-item--active');
      btn.classList.add('andrade-collection-filters__link--active');
      btn.setAttribute('aria-pressed', 'true');
    }

    this.#applyFilters(true);
  }

  #productMatches(row, materialSel, stoneSel, promoSel) {
    const materialOk =
      materialSel.size === 0 || row.material.some((k) => materialSel.has(k));
    const stoneOk = stoneSel.size === 0 || row.stone.some((k) => stoneSel.has(k));
    const promoOk =
      promoSel.size === 0 || (row.promo || []).some((k) => promoSel.has(k));
    return materialOk && stoneOk && promoOk;
  }

  #countMatches(type, key, materialSel, stoneSel, promoSel = new Set()) {
    const collectionCount = Number(this.dataset.collectionCount || 0);
    if (
      this.#isSilverOccasion() &&
      type === 'material' &&
      key === 'silver-925' &&
      materialSel.size === 0 &&
      stoneSel.size === 0 &&
      promoSel.size === 0 &&
      collectionCount > 0
    ) {
      return collectionCount;
    }

    const material = type === 'material' ? new Set([key]) : new Set(materialSel);
    const stone = type === 'stone' ? new Set([key]) : new Set(stoneSel);
    const promo = type === 'promo' ? new Set([key]) : new Set(promoSel);
    const rows = this.#getIndexRows();
    const source = rows.length ? rows : this.#getCardRows();
    return source.filter((row) => this.#productMatches(row, material, stone, promo)).length;
  }

  #applyFilters(syncUrl) {
    const rows = this.#getCardRows();
    const hasMaterial = this.#selectedMaterial.size > 0;
    const hasStone = this.#selectedStone.size > 0;
    const hasPromo = this.#selectedPromo.size > 0;
    let visible = 0;

    rows.forEach((row) => {
      const show = this.#productMatches(
        row,
        this.#selectedMaterial,
        this.#selectedStone,
        this.#selectedPromo
      );
      if (row.el) {
        row.el.hidden = !show;
        row.el.classList.toggle('andrade-filter-hidden', !show);
      }
      if (show) visible += 1;
    });

    this.#updateBadges('material', hasMaterial);
    this.#updateBadges('stone', hasStone);
    this.#updateBadges('promo', hasPromo);
    this.#updateEmptyState(visible, hasMaterial || hasStone || hasPromo);

    if (syncUrl) this.#syncUrl();
  }

  #updateBadges(type, showBadges) {
    this.querySelectorAll(`[data-filter-type="${type}"][data-filter-key]`).forEach((btn) => {
      const badge = btn.querySelector('.andrade-collection-filters__swatch-badge');
      if (!badge) return;

      if (!showBadges) {
        badge.hidden = true;
        return;
      }

      const key = btn.dataset.filterKey;
      const otherMaterial = type === 'material' ? new Set() : this.#selectedMaterial;
      const otherStone = type === 'stone' ? new Set() : this.#selectedStone;
      const otherPromo = type === 'promo' ? new Set() : this.#selectedPromo;
      const count = this.#countMatches(type, key, otherMaterial, otherStone, otherPromo);

      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
  }

  #updateEmptyState(visible, filtered) {
    const grid = this.#getGrid();
    if (!grid) return;

    let empty = grid.parentElement?.querySelector('[data-andrade-filter-empty]');
    if (!filtered) {
      empty?.remove();
      return;
    }

    if (visible > 0) {
      empty?.remove();
      return;
    }

    if (!empty) {
      empty = document.createElement('p');
      empty.dataset.andradeFilterEmpty = '';
      empty.className = 'andrade-collection-filters__empty';
      empty.textContent = 'Nenhum produto encontrado com os filtros selecionados.';
      grid.parentElement?.appendChild(empty);
    }
  }

  #syncUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('material');
    url.searchParams.delete('pedra');
    url.searchParams.delete('promo');
    url.searchParams.delete('filter.p.tag');

    if (this.#selectedMaterial.size) {
      url.searchParams.set('material', [...this.#selectedMaterial].join(','));
    }
    if (this.#selectedStone.size) {
      url.searchParams.set('pedra', [...this.#selectedStone].join(','));
    }
    if (this.#selectedPromo.size) {
      url.searchParams.set('promo', [...this.#selectedPromo].join(','));
    }

    [...this.#selectedMaterial].forEach((key) => {
      url.searchParams.append('filter.p.tag', `material:${key}`);
    });
    [...this.#selectedStone].forEach((key) => {
      url.searchParams.append('filter.p.tag', `pedra:${key}`);
    });
    [...this.#selectedPromo].forEach((key) => {
      url.searchParams.append('filter.p.tag', `promo:${key}`);
    });

    window.history.replaceState({}, '', url.toString());
  }

  #restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);

    params.getAll('filter.p.tag').forEach((tag) => {
      const lower = tag.toLowerCase();
      if (lower.startsWith('material:')) this.#selectedMaterial.add(lower.slice(9));
      if (lower.startsWith('pedra:')) this.#selectedStone.add(lower.slice(6));
      if (lower.startsWith('promo:')) this.#selectedPromo.add(lower.slice(6));
      if (lower.startsWith('promocao:')) this.#selectedPromo.add(lower.slice(9));
      if (lower.startsWith('campanha:')) this.#selectedPromo.add(lower.slice(9));
    });

    const materialParam = params.get('material');
    if (materialParam) {
      materialParam.split(',').filter(Boolean).forEach((key) => this.#selectedMaterial.add(key.trim()));
    }

    const stoneParam = params.get('pedra');
    if (stoneParam) {
      stoneParam.split(',').filter(Boolean).forEach((key) => this.#selectedStone.add(key.trim()));
    }

    const promoParam = params.get('promo');
    if (promoParam) {
      promoParam.split(',').filter(Boolean).forEach((key) => this.#selectedPromo.add(key.trim()));
    }

    const allowedMaterial = new Set(
      [...this.querySelectorAll('[data-filter-type="material"][data-filter-key]')].map((btn) => btn.dataset.filterKey)
    );
    [...this.#selectedMaterial].forEach((key) => {
      if (allowedMaterial.size && !allowedMaterial.has(key)) this.#selectedMaterial.delete(key);
    });

    this.querySelectorAll('[data-filter-type][data-filter-key]').forEach((btn) => {
      const type = btn.dataset.filterType;
      const key = btn.dataset.filterKey;
      const set = this.#setForType(type);
      const active = set.has(key);

      btn.classList.toggle('andrade-collection-filters__swatch-item--active', active);
      btn.classList.toggle('andrade-collection-filters__link--active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  #syncAccordion() {
    const groups = this.querySelectorAll('.andrade-collection-filters__group');
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    groups.forEach((group) => {
      if (isMobile) {
        group.removeAttribute('open');
      } else {
        group.setAttribute('open', '');
      }
    });
  }
}

if (!customElements.get('andrade-collection-filters')) {
  customElements.define('andrade-collection-filters', AndradeCollectionFilters);
}
